// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: one corner pop-up (design/assistant-engagement s08, s10, s15): Konvo · now with a menu
// and ✕, one sentence, the reason, the main button and Later. The connect invitation adds up to
// three rows built from the person's own numbers and offers "Connect · about a minute". Shared
// by the desktop corner (with a tail pointing at the icon) and the phone card above the bar. A
// suggestion that arrives with the work done (`ready`) shows as a ready card (ReadyCard.tsx).

import {CornerAgentTaskCard} from '@app/features/konvo_corner/components/CornerAgentTaskCard';
import styles from '@app/features/konvo_corner/components/CornerCard.module.css';
import {openCornerSettings} from '@app/features/konvo_corner/components/CornerSettingsLink';
import {CornerWrapUpCard} from '@app/features/konvo_corner/components/CornerWrapUpCard';
import {ReadyPopupCard} from '@app/features/konvo_corner/components/ReadyCard';
import Corner, {type CornerMenuChoice} from '@app/features/konvo_corner/state/Corner';
import {runFromPopup} from '@app/features/konvo_corner/state/ReadyActions';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import {ClaudeLogo} from '@app/features/ui/components/icons/ClaudeLogo';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {OutlookLogo} from '@app/features/ui/components/icons/OutlookLogo';
import {GlassMenu} from '@app/features/ui/glass_menu/GlassMenu';
import {
	BellSlashIcon,
	BuildingsIcon,
	CalendarDotsIcon,
	CheckSquareIcon,
	ClockIcon,
	DotsThreeIcon,
	GearIcon,
	InfoIcon,
	ListChecksIcon,
	ProhibitIcon,
	SparkleIcon,
	XIcon,
	ZzzIcon,
} from '@app/features/ui/icons/Icons';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import type {CornerKind, CornerSuggestion, Teaser} from '@fluxer/schema/src/domains/konvo/AssistantEngagement';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconClock, IconHourglassHigh, IconMoonStars} from '@tabler/icons-react';
import {clsx} from 'clsx';
import type React from 'react';
import {useState} from 'react';

const NOW = msg({message: 'Konvo · now', comment: 'Top line of a corner assistant pop-up.'});
const FOR_YOU = msg({message: 'Konvo · for you', comment: 'Top line of the connect-your-assistant invitation pop-up.'});
const MORE = msg({message: 'More options', comment: 'Menu button on a corner assistant pop-up.'});
const CLOSE = msg({message: 'Close', comment: 'Close button on a corner assistant pop-up.'});
const LATER = msg({message: 'Later', comment: 'Corner pop-up button: show it again in 30 minutes.'});
const NOT_NOW = msg({message: 'Not now', comment: 'Connect invitation pop-up: second button.'});
const CONNECT = msg({
	message: 'Connect · about a minute',
	comment: 'Connect invitation pop-up: main button (connecting the assistant takes about a minute).',
});
const SNOOZE = msg({message: 'Snooze for 1 hour', comment: 'Corner pop-up menu item.'});
const NOT_THIS_MEETING = msg({message: 'Not for this meeting', comment: 'Corner pop-up menu item (meeting prep).'});
const NOT_THIS = msg({message: 'Not for this one', comment: 'Corner pop-up menu item.'});
const SETTINGS = msg({message: 'Suggestion settings', comment: 'Corner pop-up menu item: opens Settings > Assistant.'});
const KEEP_QUESTION = msg({
	message: 'You skipped the last three. Keep meeting prep?',
	comment: 'Meeting prep pop-up, asked once after three were ignored in a row.',
});
const KEEP = msg({message: 'Keep them', comment: 'Keep meeting prep pop-ups.'});
const TURN_OFF = msg({message: 'Turn off', comment: 'Turn meeting prep pop-ups off.'});
const REGION = msg({message: 'Suggestion from Konvo', comment: 'Accessible name of a corner assistant pop-up.'});

const STOP: Record<CornerKind, MessageDescriptor> = {
	meeting_prep: msg({message: 'Stop meeting prep suggestions', comment: 'Corner pop-up menu item.'}),
	client_followup: msg({message: 'Stop client follow-up suggestions', comment: 'Corner pop-up menu item.'}),
	outstanding_work: msg({message: 'Stop outstanding work suggestions', comment: 'Corner pop-up menu item.'}),
	review_time: msg({message: 'Stop time review suggestions', comment: 'Corner pop-up menu item.'}),
	promise_due: msg({message: 'Stop promise reminders', comment: 'Corner pop-up menu item.'}),
	connect_invite: msg({message: 'Stop connect invitations', comment: 'Corner pop-up menu item.'}),
	agent_task: msg({
		message: 'Stop asking about agent chats',
		comment: 'Corner pop-up menu item (which ClickUp task an agent chat is for).',
	}),
	waiting_on_you: msg({
		message: 'Stop “waiting on you” suggestions',
		comment: 'Corner pop-up menu item: questions people asked you that are still unanswered.',
	}),
	waiting_on_them: msg({
		message: 'Stop “no answer yet” suggestions',
		comment: 'Corner pop-up menu item: questions you asked that got no answer.',
	}),
	wrap_up: msg({
		message: 'Stop the end-of-day wrap-up pop-up',
		comment: 'Corner pop-up menu item: the plan for tonight before the day ends (it still comes in the Konvo chat).',
	}),
};

const UNREAD_CLIENT_MAIL = msg({
	message: '{count, plural, one {# email from a client needs a reply} other {# emails from clients need a reply}}',
	comment: 'Connect invitation row. count is a number of unread client emails.',
});
const OUTSIDE_MEETINGS = msg({
	message:
		'{count, plural, one {# meeting with outside guests tomorrow} other {# meetings with outside guests tomorrow}}',
	comment: 'Connect invitation row. count is a number of meetings.',
});
const TASKS_DUE = msg({
	message: '{count, plural, one {# ClickUp task due this week} other {# ClickUp tasks due this week}}',
	comment: 'Connect invitation row. count is a number of tasks.',
});
const TASKS_OVERDUE = msg({
	message: '{count, plural, one {# already overdue} other {# already overdue}}',
	comment: 'Second line of the ClickUp row in the connect invitation. count is a number of tasks.',
});

export const KIND_ICON: Record<CornerKind, (size: number) => React.ReactNode> = {
	meeting_prep: (size) => <SparkleIcon size={size} weight="bold" />,
	client_followup: (size) => <BuildingsIcon size={size} weight="bold" />,
	outstanding_work: (size) => <ListChecksIcon size={size} weight="bold" />,
	review_time: (size) => <ClockIcon size={size} weight="bold" />,
	promise_due: (size) => <CheckSquareIcon size={size} weight="bold" />,
	connect_invite: (size) => <ClaudeLogo size={size} />,
	agent_task: (size) => <ClickUpLogo size={size} />,
	waiting_on_you: (size) => <IconClock size={size} stroke={2.2} />,
	waiting_on_them: (size) => <IconHourglassHigh size={size} stroke={2.2} />,
	wrap_up: (size) => <IconMoonStars size={size} stroke={2.2} />,
};

export interface TeaserRow {
	key: string;
	icon: React.ReactNode;
	title: string;
	detail: string | null;
}

/** Up to three rows from the person's own numbers; rows with nothing to say are left out. */
export function teaserRows(teaser: Teaser | undefined, i18n: I18n): Array<TeaserRow> {
	if (!teaser) return [];
	const rows: Array<TeaserRow> = [];
	if (teaser.unread_client_mail > 0)
		rows.push({
			key: 'mail',
			icon: <OutlookLogo size={18} />,
			title: i18n._(UNREAD_CLIENT_MAIL, {count: teaser.unread_client_mail}),
			detail: teaser.client_names.length ? teaser.client_names.slice(0, 3).join(', ') : null,
		});
	if (teaser.outside_meetings_tomorrow > 0)
		rows.push({
			key: 'calendar',
			icon: <CalendarDotsIcon size={18} />,
			title: i18n._(OUTSIDE_MEETINGS, {count: teaser.outside_meetings_tomorrow}),
			detail: teaser.meeting_names.length ? teaser.meeting_names.slice(0, 3).join(', ') : null,
		});
	if (teaser.tasks_due_week > 0)
		rows.push({
			key: 'tasks',
			icon: <ClickUpLogo size={18} />,
			title: i18n._(TASKS_DUE, {count: teaser.tasks_due_week}),
			detail: teaser.tasks_overdue > 0 ? i18n._(TASKS_OVERDUE, {count: teaser.tasks_overdue}) : null,
		});
	return rows.slice(0, 3);
}

function menuItems(suggestion: CornerSuggestion, i18n: I18n) {
	const items: Array<{choice: CornerMenuChoice; label: string; icon: React.ReactNode}> = [
		{choice: 'snooze', label: i18n._(SNOOZE), icon: <ZzzIcon size={18} />},
		{
			choice: 'not_this',
			label: i18n._(suggestion.kind === 'meeting_prep' ? NOT_THIS_MEETING : NOT_THIS),
			icon: suggestion.kind === 'meeting_prep' ? <CalendarDotsIcon size={18} /> : <ProhibitIcon size={18} />,
		},
		{choice: 'stop', label: i18n._(STOP[suggestion.kind]), icon: <BellSlashIcon size={18} />},
		{choice: 'settings', label: i18n._(SETTINGS), icon: <GearIcon size={18} />},
	];
	return items;
}

export function CornerCard({
	suggestion,
	variant,
	className,
}: {
	suggestion: CornerSuggestion;
	variant: 'desktop' | 'phone';
	className?: string;
}) {
	const {i18n} = useLingui();
	// Phone: the glass menu drops from the ⋯ that was pressed (it was a bottom sheet).
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
	// An agent chat without a ClickUp task: Konvo's options, linked in place.
	if (suggestion.kind === 'agent_task')
		return <CornerAgentTaskCard suggestion={suggestion} variant={variant} className={className} />;
	// The end-of-day wrap-up: the same plan card as in the Konvo chat, acted on in place.
	if (suggestion.kind === 'wrap_up')
		return <CornerWrapUpCard suggestion={suggestion} variant={variant} className={className} />;
	const invite = suggestion.kind === 'connect_invite';
	const rows = invite ? teaserRows(suggestion.teaser, i18n) : [];
	const items = menuItems(suggestion, i18n);
	// The menu acts on this suggestion even if its card closed while the menu was open.
	const choose = (choice: CornerMenuChoice) => Corner.menu(choice, openCornerSettings, suggestion);

	const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		// The menu sits outside the card: keep the card up while it is open.
		Corner.menuOpened(true);
		if (MobileLayout.enabled) {
			setMenuAnchor(event.currentTarget);
			return;
		}
		ContextMenuCommands.openFromElementBottomRight(
			event,
			() => (
				<MenuGroup>
					{items.map((item) => (
						<MenuItem key={item.choice} icon={item.icon} onClick={() => choose(item.choice)}>
							{item.label}
						</MenuItem>
					))}
				</MenuGroup>
			),
			{onClose: () => Corner.menuOpened(false)},
		);
	};
	const closeSheet = () => {
		setMenuAnchor(null);
		Corner.menuOpened(false);
	};
	const menuSheet = variant === 'phone' && !invite && (
		<GlassMenu
			isOpen={menuAnchor != null}
			onClose={closeSheet}
			anchor={menuAnchor}
			data-flx="konvo-corner.card.menu-sheet"
			groups={[
				{
					items: items.map((item) => ({
						label: item.label,
						icon: item.icon,
						onClick: () => {
							closeSheet();
							choose(item.choice);
						},
					})),
				},
			]}
		/>
	);

	// The work is done already (design/suggestion-stories): the ready card, with the same menu and ✕.
	if (suggestion.ready)
		return (
			<ReadyPopupCard
				card={suggestion.ready}
				variant={variant === 'phone' ? 'phone' : 'popup'}
				kind={suggestion.kind}
				onAction={(action) => runFromPopup(i18n, suggestion, action)}
				onClose={() => Corner.dismiss()}
				onMenu={openMenu}
				onHover={(entering) => Corner.hover(entering)}
				className={className}
			>
				{menuSheet}
			</ReadyPopupCard>
		);

	return (
		// Announced by the always-mounted live region in CornerAssistant; focusable (Alt+Shift+K) but
		// it never takes focus by itself. Escape dismisses it.
		<section
			className={clsx(styles.card, variant === 'phone' ? styles.phone : styles.desktop, className)}
			aria-label={i18n._(REGION)}
			aria-keyshortcuts="Alt+Shift+K"
			tabIndex={-1}
			onMouseEnter={() => Corner.hover(true)}
			onMouseLeave={() => Corner.hover(false)}
			data-kind={suggestion.kind}
			data-flx="konvo-corner.card"
		>
			<div className={styles.top}>
				<KonvoLogo size={18} />
				<span className={styles.context}>{i18n._(invite ? FOR_YOU : NOW)}</span>
				{!invite && (
					<button
						type="button"
						className={styles.iconButton}
						aria-label={i18n._(MORE)}
						onClick={openMenu}
						data-flx="konvo-corner.card.menu"
					>
						<DotsThreeIcon size={18} weight="bold" />
					</button>
				)}
				<button
					type="button"
					className={styles.iconButton}
					aria-label={i18n._(CLOSE)}
					onClick={() => Corner.dismiss()}
					data-flx="konvo-corner.card.close"
				>
					<XIcon size={16} />
				</button>
			</div>
			<p className={clsx(styles.title, invite && styles.titleLarge)} data-flx="konvo-corner.card.title">
				{suggestion.title}
			</p>
			{rows.length > 0 && (
				<ul className={styles.rows} data-flx="konvo-corner.card.teaser">
					{rows.map((row) => (
						<li key={row.key} className={styles.row} data-row={row.key}>
							<span className={styles.rowIcon}>{row.icon}</span>
							<span className={styles.rowText}>
								<span className={styles.rowTitle}>{row.title}</span>
								{row.detail && <span className={styles.rowDetail}>{row.detail}</span>}
							</span>
						</li>
					))}
				</ul>
			)}
			{suggestion.reason && (
				<p className={styles.reason} data-flx="konvo-corner.card.reason">
					{invite ? (
						<SparkleIcon size={14} className={styles.reasonIcon} />
					) : (
						<InfoIcon size={14} className={styles.reasonIcon} />
					)}
					<span>{suggestion.reason}</span>
				</p>
			)}
			<div className={styles.actions}>
				<button
					type="button"
					className={styles.primary}
					onClick={() => Corner.main()}
					data-flx="konvo-corner.card.main"
				>
					{KIND_ICON[suggestion.kind](16)}
					<span>{invite ? i18n._(CONNECT) : suggestion.primary_label}</span>
				</button>
				<button
					type="button"
					className={styles.secondary}
					onClick={() => (invite ? Corner.notNow() : Corner.later())}
					data-flx="konvo-corner.card.later"
				>
					{i18n._(invite ? NOT_NOW : LATER)}
				</button>
			</div>
			{suggestion.ask_keep && suggestion.kind === 'meeting_prep' && (
				<div className={styles.keep} data-flx="konvo-corner.card.ask-keep">
					<span className={styles.keepText}>{i18n._(KEEP_QUESTION)}</span>
					<button
						type="button"
						className={styles.keepButton}
						onClick={() => Corner.keep()}
						data-flx="konvo-corner.card.keep"
					>
						{i18n._(KEEP)}
					</button>
					<button
						type="button"
						className={styles.keepButton}
						onClick={() => Corner.menu('stop', openCornerSettings, suggestion)}
						data-flx="konvo-corner.card.turn-off"
					>
						{i18n._(TURN_OFF)}
					</button>
				</div>
			)}
			{menuSheet}
		</section>
	);
}
